import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  IconButton,
  Badge,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Button,
  CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import notificationStore from '../../stores/notificationStore';
import {
  NotificationResponseRecovery,
  applyNotificationOutcome,
  submitNotificationResponse,
} from './notificationResponseClient';

/**
 * Notification Center
 * 
 * Bell icon with badge count in header.
 * Click opens right-side drawer with notification history.
 * Shows chronological list with severity indicators and ack status.
 */
const NotificationCenter = ({
  onNotificationClick,
  buttonSx,
  buttonTestId,
  navControl = false,
}) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [respondingIds, setRespondingIds] = useState(new Set());

  useEffect(() => {
    // Initial load
    setNotifications(notificationStore.getAll());
    setBadgeCount(notificationStore.getBadgeCount());

    // Subscribe to changes
    const unsubscribe = notificationStore.subscribe((updatedNotifications) => {
      setNotifications([...updatedNotifications]);
      setBadgeCount(notificationStore.getBadgeCount());
    });

    return unsubscribe;
  }, []);

  const handleToggle = () => {
    setOpen(!open);
    
    // Mark all non-critical as read when opening
    if (!open) {
      notifications
        .filter(n => n.severity !== 'critical' && !n.read)
        .forEach(n => notificationStore.markAsRead(n.id));
    }
  };

  const handleNotificationClick = (notification) => {
    console.log('[NOTIF-CENTER] Clicked notification:', notification.id);
    
    // If critical and unacknowledged, trigger modal
    if (notification.severity === 'critical' && !notification.acknowledged) {
      if (onNotificationClick) {
        onNotificationClick(notification);
      }
    } else {
      // Mark as read
      notificationStore.markAsRead(notification.id);
    }
  };

  const handleResponseClick = useCallback(async (notification, responseKey) => {
    const notifId = notification.id;
    const actionId = notification.actionId;

    if (!actionId) {
      console.warn('[NOTIF-CENTER] No actionId on notification, cannot respond:', notifId);
      return;
    }

    if (respondingIds.has(notifId)) return;
    setRespondingIds(prev => new Set(prev).add(notifId));
    notificationStore.markAsResponded(notifId, 'pending');
    const outcome = await submitNotificationResponse({ notification, responseKey });
    applyNotificationOutcome(notifId, outcome);
    setRespondingIds(prev => {
      const next = new Set(prev);
      next.delete(notifId);
      return next;
    });
  }, [respondingIds]);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
      case 'error':
        return <ErrorOutlineIcon color="error" />;
      case 'warning':
        return <WarningAmberIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleToggle}
        aria-label="Notifications"
        data-testid={buttonTestId}
        data-nav-control={navControl ? 'true' : undefined}
        data-tooltip="Notifications"
        sx={buttonSx}
      >
        <Badge badgeContent={badgeCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleToggle}
        PaperProps={{
          sx: { width: 400 }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Notifications</Typography>
          <IconButton onClick={handleToggle} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getSeverityIcon(notification.severity)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {notification.title}
                        </Typography>
                        {notification.severity === 'critical' && (
                          notification.acknowledged ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Acknowledged"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Requires Ack"
                              size="small"
                              color="error"
                            />
                          )
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(notification.timestamp).toLocaleString()}
                        </Typography>
                        {/* Response buttons */}
                        {notification.responses && notification.responses.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {notification.responseStatus === 'accepted' && (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label={notification.responseMessage || 'Response sent'}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                            {notification.responseStatus === 'rejected' && (
                              <Chip
                                label={notification.responseMessage || 'Already handled'}
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            )}
                            {notification.responseStatus === 'error' && (
                              <Chip
                                label={notification.responseMessage || 'Error'}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                            <NotificationResponseRecovery
                              notification={notification}
                              disabled={respondingIds.has(notification.id)}
                              onRetry={(responseKey) => handleResponseClick(notification, responseKey)}
                            />
                            {(!notification.responseStatus || notification.responseStatus === 'error') && (
                              notification.responses.map((resp) => (
                                <Button
                                  key={resp.responseKey}
                                  size="small"
                                  variant="outlined"
                                  disabled={
                                    notification.responseStatus === 'pending' ||
                                    !notification.actionId
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResponseClick(notification, resp.responseKey);
                                  }}
                                  startIcon={
                                    notification.responseStatus === 'pending' && respondingIds.has(notification.id)
                                      ? <CircularProgress size={14} />
                                      : undefined
                                  }
                                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  {resp.responseLabel || resp.responseKey}
                                </Button>
                              ))
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => {
                  notificationStore.clear();
                }}
              >
                Clear All
              </Button>
            </Box>
          </>
        )}
      </Drawer>
    </>
  );
};

export default NotificationCenter;
